import { useEffect, useRef } from 'react';

/**
 * Keyboard-shortcut help overlay for the Reader.
 *
 * Opened by pressing `?` (or `Shift+/` on most layouts). Closed by:
 *   - Pressing Esc
 *   - Pressing `?` again
 *   - Clicking the backdrop
 *   - Clicking the close button
 *
 * Keep this list in sync with the actual handlers in Reader.tsx. If
 * we add a shortcut there, add it here. The whole overlay is decorative
 * — closing it never blocks the user from anything.
 */

type Shortcut = {
  keys: string[];
  label: string;
};

const SHORTCUTS: { group: string; items: Shortcut[] }[] = [
  {
    group: 'Navigation',
    items: [
      { keys: ['←'], label: 'Previous ayah' },
      { keys: ['→'], label: 'Next ayah' },
    ],
  },
  {
    group: 'Audio',
    items: [
      { keys: ['Space'], label: 'Play / pause' },
      { keys: ['K'], label: 'Play / pause' },
    ],
  },
  {
    group: 'Reading progress',
    items: [
      { keys: ['Shift', 'R'], label: 'Mark current ayah as read' },
    ],
  },
  {
    group: 'This panel',
    items: [
      { keys: ['?'], label: 'Show / hide this help' },
      { keys: ['Esc'], label: 'Close any dialog' },
    ],
  },
];

export function ShortcutsOverlay({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-overlay-title"
      className="
        fixed inset-0 z-50 flex items-center justify-center
        bg-night-950/80 backdrop-blur-sm
        motion-safe:animate-in motion-safe:fade-in
      "
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="
          relative mx-4 w-full max-w-md
          rounded-2xl border border-night-500/40 bg-night-800 p-6
          shadow-2xl
        "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2
            id="shortcuts-overlay-title"
            className="font-display text-lg font-bold text-night-50"
          >
            Keyboard shortcuts
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="
              flex h-8 w-8 items-center justify-center
              rounded-full text-night-300
              transition-colors hover:bg-night-700 hover:text-night-50
              focus:outline-none focus:ring-2 focus:ring-beam/40
            "
            aria-label="Close shortcuts help"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5">
          {SHORTCUTS.map((group) => (
            <section key={group.group}>
              <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-beam/80">
                {group.group}
              </h3>
              <dl className="space-y-1.5">
                {group.items.map((item) => (
                  <div
                    key={item.label + item.keys.join('+')}
                    className="flex items-center justify-between gap-4"
                  >
                    <dt className="text-sm text-night-100">{item.label}</dt>
                    <dd className="flex shrink-0 items-center gap-1">
                      {item.keys.map((k, idx) => (
                        <span
                          key={k + idx}
                          className="flex items-center gap-1"
                        >
                          <kbd
                            className="
                              inline-flex h-6 min-w-[1.5rem] items-center justify-center
                              rounded-md border border-night-500/50 bg-night-700
                              px-1.5 text-[11px] font-mono font-medium text-night-100
                              shadow-[inset_0_-1px_0_0_rgb(0_0_0/0.3)]
                            "
                          >
                            {k}
                          </kbd>
                          {idx < item.keys.length - 1 && (
                            <span className="text-[10px] text-night-400">+</span>
                          )}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <p className="mt-6 text-[10px] uppercase tracking-wide text-night-300/70">
          Shortcuts are paused while typing in any form field.
        </p>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
